import { FC } from 'react'
import { Box, ColumnLayout, SpaceBetween } from '@cloudscape-design/components'
import useSingleTags from '../../../hooks/query/useSingleTags'
import LoadingSpinner from '../../LoadingSpinner'
import ApproverOverview from '../ApproverOverview'
import { keysToLabel } from '../../../helper/toCamelCase'
import type { PlatformOwnerResource } from '../../../helper/platformOwnerPermissions'

interface ReadGenericTagsProps {
	tagsId: string
	layout?: number
	isApprovalDisabled?: boolean
	allowPlatformOwnerApproval?: boolean
	platformOwnerResource?: PlatformOwnerResource
	platformOwnerEmail?: string
	showApprovalStatus?: boolean
}

const ReadGenericTags: FC<ReadGenericTagsProps> = ({
	tagsId,
	layout = 2,
	isApprovalDisabled = true,
	allowPlatformOwnerApproval = false,
	platformOwnerResource,
	platformOwnerEmail,
	showApprovalStatus = true
}) => {
	const { data: originalTags, isLoading: isLoadingTags } = useSingleTags(tagsId)

	if (!originalTags || isLoadingTags) return <LoadingSpinner />

	const hiddenKeys = new Set(['applications', 'fundingChoice', 'parentTagsID'])
	const tagKeys = Object.keys(originalTags['details'] || {}).filter(
		(key) => !hiddenKeys.has(key)
	)
	const getLabel = (tagkey: string) => {
		switch (tagkey) {
			case 'tagProject':
				return 'Project'
			case 'WBSCode':
			case 'W B S Code':
				return 'WBS Code'
			default:
				return keysToLabel(tagkey)
		}
	}
	/** Safely render detail value: may be string or { value, label } */
	const formatDetailValue = (val: unknown): string => {
		if (val == null || val === '') return 'NA'
		if (typeof val === 'object' && val !== null && ('value' in val || 'label' in val)) {
			const o = val as { value?: string; label?: string }
			return o.label ?? o.value ?? 'NA'
		}
		return String(val)
	}
	return (
		<>
			<SpaceBetween direction="vertical" size="s">
				<ColumnLayout columns={layout} variant="text-grid">
					{tagKeys?.map((tagkey) => (
						<div key={tagkey}>
							<Box variant="awsui-key-label">{getLabel(tagkey)}</Box>
							{formatDetailValue(originalTags['details'][tagkey])}
						</div>
					))}
				</ColumnLayout>
				{showApprovalStatus && (
				<ApproverOverview
					approvalStatus={originalTags['approvalStatus']}
					isDisabled={isApprovalDisabled}
					allowPlatformOwnerApproval={allowPlatformOwnerApproval}
					platformOwnerResource={platformOwnerResource}
					tagsID={tagsId}
					tagDetails={{
						...originalTags.details,
						platformOwnerEmail:
							platformOwnerEmail || originalTags.details?.platformOwnerEmail
					}}
				/>
				)}
			</SpaceBetween>
		</>
	)
}

export default ReadGenericTags
